import React, { useMemo, useState } from "react";
import {
  Typography,
  Box,
  Stack,
  Button,
  TextField,
  IconButton,
  InputAdornment,
} from "@mui/material";
import LockOutlined from "@mui/icons-material/LockOutlined";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import CheckCircleRounded from "@mui/icons-material/CheckCircleRounded";
import RadioButtonUncheckedRounded from "@mui/icons-material/RadioButtonUncheckedRounded";
import { CommonLayout, FormWrapper } from "../components/CommonLayout";
import NotificationBanner from "../components/common/NotificationBanner";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useNavigate, useSearchParams } from "react-router-dom";
import { completeActivatePassword, completeResetPassword } from "../services/password.service";

const SPECIAL_CHAR_REGEX = /[^A-Za-z0-9\s]/;

const passwordSchema = Yup.string()
  .required("Por favor ingresa tu contraseña")
  .min(8, "Debe tener al menos 8 caracteres")
  .max(16, "Debe tener máximo 16 caracteres")
  .matches(/[A-Z]/, "Debe incluir al menos una letra mayúscula")
  .matches(/[a-z]/, "Debe incluir al menos una letra minúscula")
  .matches(/[0-9]/, "Debe incluir al menos un número")
  .matches(SPECIAL_CHAR_REGEX, "Debe incluir al menos un carácter especial");

const CreatePasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const isChange = (searchParams.get("mode") || "").toLowerCase() === "change";
  const token = (searchParams.get("token") || "").trim();
  const code = (searchParams.get("code") || "").trim();
  const navigate = useNavigate();

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    type: "success",
    title: "",
    message: "",
  });

  const validationSchema = Yup.object({
    password: passwordSchema,
    confirmPassword: Yup.string()
      .required("Por favor confirma tu contraseña")
      .oneOf([Yup.ref("password")], "Las contraseñas no coinciden"),
  });

  const rules = useMemo(
    () => [
      {
        key: "len",
        label: "De 8 a 16 caracteres",
        test: (v: string) => v.length >= 8 && v.length <= 16,
      },
      { key: "up", label: "Al menos una letra mayúscula", test: (v: string) => /[A-Z]/.test(v) },
      { key: "low", label: "Al menos una letra minúscula", test: (v: string) => /[a-z]/.test(v) },
      { key: "num", label: "Al menos un número", test: (v: string) => /[0-9]/.test(v) },
      {
        key: "spe",
        label: "Al menos un carácter especial",
        test: (v: string) => SPECIAL_CHAR_REGEX.test(v),
      },
    ],
    []
  );

  const handleSubmit = async (
    values: { password: string; confirmPassword: string },
    { setSubmitting, resetForm }: any
  ) => {
    try {
      if (!token) throw new Error("Falta el token de verificación para completar el proceso");
      if (!code) throw new Error("Falta el código de verificación para completar el proceso");

      if (isChange) {
        await completeResetPassword(code, values.password, values.confirmPassword, token);
      } else {
        await completeActivatePassword(code, values.password, values.confirmPassword, token);
      }
      setNotification({
        open: true,
        type: "success",
        title: isChange ? "Contraseña cambiada" : "Contraseña creada",
        message: isChange
          ? "Tu contraseña fue actualizada correctamente."
          : "Tu contraseña fue creada correctamente.",
      });
      resetForm();
      setTimeout(() => navigate("/"), 1800);
    } catch (e) {
      setNotification({
        open: true,
        type: "error",
        title: "Error",
        message: (e as any)?.response?.data?.message || "No se pudo procesar la solicitud. Intenta nuevamente.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <CommonLayout>
      <FormWrapper elevation={3}>
        <Stack spacing={3} sx={{ width: "100%" }}>
          <Box sx={{ width: "100%", textAlign: "left" }}>
            <Stack spacing={1} direction="row" alignItems="center">
              <Typography fontSize={24} fontWeight={700}>
                {isChange ? "Cambiar contraseña" : "Crear Contraseña"}
              </Typography>
              <LockOutlined fontSize="small" />
            </Stack>
            {isChange && (
              <Typography color="#bdbdbd" fontWeight={400} sx={{ mt: 0.5 }}>
                Tu nueva contraseña debe ser diferente a las anteriores.
              </Typography>
            )}
          </Box>

          <Formik
            initialValues={{ password: "", confirmPassword: "" }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
              <Form style={{ width: "100%" }}>
                <Stack spacing={2}>
                  <TextField
                    name="password"
                    type={showPassword ? "text" : "password"}
                    value={values.password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder={isChange ? "Nueva Contraseña" : "Ingresar Contraseña"}
                    fullWidth
                    autoComplete="new-password"
                    error={Boolean(touched.password && errors.password)}
                    helperText={touched.password && errors.password ? errors.password : " "}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={() => setShowPassword((s) => !s)}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    name="confirmPassword"
                    type={showConfirm ? "text" : "password"}
                    value={values.confirmPassword}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder={isChange ? "Confirmar Contraseña" : "Confirmar Contraseña"}
                    fullWidth
                    autoComplete="new-password"
                    error={Boolean(touched.confirmPassword && errors.confirmPassword)}
                    helperText={
                      touched.confirmPassword && errors.confirmPassword
                        ? (errors.confirmPassword as string)
                        : " "
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle confirm password visibility"
                            onClick={() => setShowConfirm((s) => !s)}
                            edge="end"
                          >
                            {showConfirm ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Box>
                    <Stack spacing={0.75}>
                      {rules.map((r) => {
                        const ok = r.test(values.password || "");
                        const color = ok ? "#002E74" : "#9e9e9e";
                        return (
                          <Stack direction="row" spacing={1} alignItems="center" key={r.key}>
                            {ok ? (
                              <CheckCircleRounded sx={{ fontSize: 18, color }} />
                            ) : (
                              <RadioButtonUncheckedRounded sx={{ fontSize: 18, color }} />
                            )}
                            <Typography color={color} fontSize={14}>
                              {r.label}
                            </Typography>
                          </Stack>
                        );
                      })}
                    </Stack>
                  </Box>

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    sx={{
                      py: 1.5,
                      fontWeight: "bold",
                      backgroundColor: "#002E74",
                      color: "white",
                      fontSize: 18,
                      "&:hover": { backgroundColor: "#001F4D" },
                    }}
                    disabled={isSubmitting}
                  >
                    {isChange ? "Cambiar Contraseña" : "Crear Contraseña"}
                  </Button>
                </Stack>
              </Form>
            )}
          </Formik>

          <NotificationBanner
            type={notification.type as "error" | "success" | "warning" | "info"}
            title={notification.title}
            message={notification.message}
            open={notification.open}
            onClose={() => setNotification((p) => ({ ...p, open: false }))}
          />
        </Stack>
      </FormWrapper>
    </CommonLayout>
  );
};

export default CreatePasswordPage;
